import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, inject, Injector } from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxCustomAddress } from '@onyx/angular';
import {
  ActionHelper,
  OnyxAutoFocusDirective,
  FormHelper,
  OnyxAddressInputComponent,
  OnyxButtonComponent,
  OnyxFormMode,
  OnyxMessageComponent,
  OnyxModalComponent,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import { chain } from 'lodash';
import { Subject } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { ContractorsRoute } from '../../../common/enums/contractors-route';

export type ContractorBranchFormGroup = ReturnType<
  typeof ContractorBranchFormModalComponent.buildForm
>;
export type ContractorBranchForm = ReturnType<
  ContractorBranchFormGroup['getRawValue']
>;

export interface ContractorBranchFormModalData {
  mode: OnyxFormMode;
  form: ContractorBranchFormGroup;
}

export type ContractorBranchFormModalResult = ContractorBranchFormGroup;

@Component({
  selector: 'app-contractor-branch-form-modal',
  imports: [
    OnyxModalComponent,
    TranslatePipe,
    OnyxMessageComponent,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxAddressInputComponent,
    OnyxButtonComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './contractor-branch-form-modal.component.html',
  styleUrl: './contractor-branch-form-modal.component.scss',
})
export class ContractorBranchFormModalComponent {
  protected readonly data = inject<ContractorBranchFormModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(
    DialogRef<ContractorBranchFormModalResult | void>,
  );
  private readonly injector = inject(Injector);

  protected readonly I18N_FORM = 'contractors.contractorForm';
  protected readonly I18N = `${this.I18N_FORM}.branches`;

  protected readonly OnyxFormMode = OnyxFormMode;

  protected close$ = new Subject<ContractorBranchFormModalResult | void>();

  protected addContractor(): void {
    ActionHelper.openRoute(ContractorsRoute.ADD_CONTRACTOR);
  }

  protected reset(): void {
    const addressControls = this.data.form.controls.addresses.controls;
    const mainAddressControl = addressControls.mainAddress;
    const mainAddress = mainAddressControl.getRawValue();

    FormHelper.reset([
      addressControls.correspondenceAddress,
      ...(mainAddressControl.disabled ? [] : [mainAddressControl]),
    ]);

    if (mainAddressControl.disabled) mainAddressControl.setValue(mainAddress);
  }

  protected async submit(): Promise<void> {
    const form = this.data.form;
    const controls = chain(form.controls)
      .pick(['name', 'addresses'])
      .values()
      .compact()
      .value();
    if (!(await ValidationHelper.checkValidity(controls, this.injector))) {
      return;
    }

    this.close$.next(form);
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    options?: { main: boolean },
  ) {
    return fb.group({
      ...(!options?.main && {
        uuid: fb.control(uuidv7()),
        name: fb.control<string | null>(null, [
          Validators.required,
          onyxTextFieldLengthValidator,
        ]),
      }),
      addresses: fb.group({
        mainAddress: fb.control<OnyxCustomAddress | null>(null, [
          Validators.required,
        ]),
        correspondenceAddress: fb.control<OnyxCustomAddress | null>(null),
      }),
    });
  }
}
